import { fnv1a32 } from "../../shared/hashing/fnv1a";

export interface AnnotationLabelOption {
  name: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  ringColor: string;
  barColor: string;
}

/** Where a label list's colours start, and how far apart its labels' hues lie: the golden angle,
 * so however long the list, neighbours and near-neighbours never look alike. */
const FIRST_HUE = 210;
const GOLDEN_ANGLE = 137.508;

function optionOfHue(name: string, hue: number): AnnotationLabelOption {
  return {
    name,
    backgroundColor: `hsl(${hue} 82% 94%)`,
    textColor: `hsl(${hue} 72% 24%)`,
    borderColor: `hsl(${hue} 70% 78%)`,
    ringColor: `hsl(${hue} 72% 70%)`,
    barColor: `hsl(${hue} 72% 46%)`,
  };
}

/**
 * A label's colours from its name alone, for a label no list on screen names (a deprecated one,
 * or one shown before its list loads): its name hashed to a hue. Two such labels may look alike.
 * @param labelName the name of the annotation label
 * @returns the color scheme for the annotation label
 */
export function getAnnotationLabelOption(labelName: string): AnnotationLabelOption {
  const normalizedName = labelName || "Annotation";
  return optionOfHue(normalizedName, fnv1a32(normalizedName) % 360);
}

/** A label list's labels, in its order, each coloured by its place in the list, so no two of a
 * list's labels share a colour (R0-07: hashed names put Person and Place both in green). */
export function createAnnotationLabelOptions(
  labelNames: readonly string[]
): AnnotationLabelOption[] {
  return labelNames.map((name, index) => optionOfHue(name || "Annotation", Math.round((FIRST_HUE + index * GOLDEN_ANGLE) % 360)));
}

/** The colours of label `name`: its list's, else its own from its name. */
export function labelOptionIn(options: readonly AnnotationLabelOption[], name: string): AnnotationLabelOption {
  return options.find((option) => option.name === name) ?? getAnnotationLabelOption(name);
}
